/**
 * Timer Manager
 * 
 * Handles all game timers for construction, research, and other timed events.
 */

export class TimerManager {
    constructor() {
        this.timers = new Map(); // Map of timer IDs to timer objects
        this.nextTimerId = 1;
    }
    
    /**
     * Create a new timer
     * @param {string} type - Timer type ('construction', 'research', etc.)
     * @param {number} durationSeconds - Duration in seconds
     * @param {Function} onComplete - Callback when timer completes
     * @param {Object} data - Additional data to store with timer
     * @returns {number} Timer ID
     */
    createTimer(type, durationSeconds, onComplete, data = {}) {
        const timerId = this.nextTimerId++;
        const now = Date.now();
        
        const timer = {
            id: timerId,
            type,
            startTime: now,
            duration: durationSeconds * 1000, // Convert to milliseconds
            endTime: now + (durationSeconds * 1000),
            onComplete,
            data,
            paused: false,
            pausedAt: null,
            remainingWhenPaused: null
        };
        
        this.timers.set(timerId, timer);
        return timerId;
    }
    
    /**
     * Update all timers (call this every frame/second)
     * @param {number} deltaTime - Time elapsed in milliseconds
     */
    update(deltaTime) {
        const now = Date.now();
        const completedTimers = [];
        
        for (const [timerId, timer] of this.timers.entries()) {
            if (timer.paused) {
                continue; // Skip paused timers
            }
            
            // Check if timer has completed
            if (now >= timer.endTime) {
                completedTimers.push(timer);
            }
        }
        
        // Execute callbacks for completed timers
        for (const timer of completedTimers) {
            this.timers.delete(timer.id);
            if (timer.onComplete) {
                try {
                    timer.onComplete(timer.id, timer.data);
                } catch (error) {
                    console.error('Error in timer callback:', error);
                }
            }
        }
    }
    
    /**
     * Get remaining time for a timer in seconds
     * @param {number} timerId - Timer ID
     * @returns {number} Remaining time in seconds, or 0 if timer doesn't exist
     */
    getRemainingTime(timerId) {
        const timer = this.timers.get(timerId);
        if (!timer) return 0;
        
        if (timer.paused) {
            return (timer.remainingWhenPaused || 0) / 1000;
        }
        
        const remaining = Math.max(0, timer.endTime - Date.now());
        return remaining / 1000; // Convert to seconds
    }
    
    /**
     * Get progress (0.0 to 1.0) for a timer
     * @param {number} timerId - Timer ID
     * @returns {number} Progress from 0.0 to 1.0
     */
    getProgress(timerId) {
        const timer = this.timers.get(timerId);
        if (!timer) return 0;
        
        if (timer.paused) {
            const remaining = timer.remainingWhenPaused || timer.duration;
            return 1.0 - (remaining / timer.duration);
        }
        
        const elapsed = Date.now() - timer.startTime;
        return Math.min(1.0, Math.max(0.0, elapsed / timer.duration));
    }
    
    /**
     * Pause a timer
     * @param {number} timerId - Timer ID
     */
    pauseTimer(timerId) {
        const timer = this.timers.get(timerId);
        if (!timer || timer.paused) return;
        
        timer.paused = true;
        timer.pausedAt = Date.now();
        timer.remainingWhenPaused = Math.max(0, timer.endTime - timer.pausedAt);
    }
    
    /**
     * Resume a paused timer
     * @param {number} timerId - Timer ID
     */
    resumeTimer(timerId) {
        const timer = this.timers.get(timerId);
        if (!timer || !timer.paused) return;
        
        timer.paused = false;
        timer.endTime = Date.now() + timer.remainingWhenPaused;
        timer.pausedAt = null;
        timer.remainingWhenPaused = null;
    }
    
    /**
     * Cancel a timer
     * @param {number} timerId - Timer ID
     */
    cancelTimer(timerId) {
        this.timers.delete(timerId);
    }
    
    /**
     * Get all timers of a specific type
     * @param {string} type - Timer type
     * @returns {Array} Array of timer objects
     */
    getTimersByType(type) {
        const result = [];
        for (const timer of this.timers.values()) {
            if (timer.type === type) {
                result.push(timer);
            }
        }
        return result;
    }
    
    /**
     * Get all active timers
     * @returns {Array} Array of timer objects
     */
    getAllTimers() {
        return Array.from(this.timers.values());
    }
    
    /**
     * Clear all timers
     */
    clearAll() {
        this.timers.clear();
    }
    
    /**
     * Pause all timers
     */
    pauseAll() {
        for (const timer of this.timers.values()) {
            if (!timer.paused) {
                this.pauseTimer(timer.id);
            }
        }
    }
    
    /**
     * Resume all timers
     */
    resumeAll() {
        for (const timer of this.timers.values()) {
            if (timer.paused) {
                this.resumeTimer(timer.id);
            }
        }
    }
}

